import { useEffect, useState, type InputHTMLAttributes } from "react";
import { centsFromInput, editableAmountFromCents } from "@mavatech/shared";
import { Input } from "./Input";

interface MoneyInputProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "value" | "onChange" | "type"
> {
  valueCents: number;
  onChangeCents: (cents: number) => void;
  fullWidth?: boolean;
}

/**
 * Geldbeträge immer mit Komma zeigen und akzeptieren — unabhängig von Browser-/OS-Locale.
 * Grund: Ein natives <input type="number"> rendert/erwartet Dezimaltrennzeichen je nach
 * Locale des Browsers (oft Punkt), während der Rest der App konsequent "€ 1.234,56" (de-AT)
 * verwendet. Das führte zu Verwechslungen beim Eintippen von Preisen (siehe AUDIT-2026-10-01-UX.md,
 * Punkt 1.2). Während der Eingabe wird der Rohtext belassen (damit Zwischenzustände wie "3,"
 * nicht weggeformatiert werden), erst beim Verlassen des Feldes wird sauber neu formatiert.
 */
export function MoneyInput({
  valueCents,
  onChangeCents,
  fullWidth = true,
  className,
  ...rest
}: MoneyInputProps) {
  const [text, setText] = useState(() => editableAmountFromCents(valueCents));
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) setText(editableAmountFromCents(valueCents));
  }, [valueCents, focused]);

  return (
    <Input
      {...rest}
      fullWidth={fullWidth}
      className={className}
      inputMode="decimal"
      value={text}
      onFocus={(e) => {
        setFocused(true);
        rest.onFocus?.(e);
      }}
      onChange={(e) => {
        setText(e.target.value);
        onChangeCents(centsFromInput(e.target.value));
      }}
      onBlur={(e) => {
        setFocused(false);
        setText(editableAmountFromCents(valueCents));
        rest.onBlur?.(e);
      }}
    />
  );
}
